import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Row, Col } from 'antd'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../state_manager/hooks/useAppDispatch'
import { useRightMenu } from '../../../../core/contexts/RightMenuContext.jsx'
import { TasksSection } from '../../../tasks/components/TasksSection.tsx'
import { InteractionsSection } from '../../../interactions/components/InteractionsSection.tsx'
import {
  DetailPanel,
  type DetailPanelItemType,
  type DetailPanelMode
} from '../../../deals/components/forms/parts/DetailPanel.tsx'
import { ProspectOverviewSection } from './sections/ProspectOverviewSection.tsx'
import { createTask, updateTask, deleteTask as deleteTaskThunk } from '../../../../store/slices/tasksSlice'
import {
  createInteraction,
  updateInteraction,
  deleteInteraction,
  fetchInteractionsForProspect,
  delinkInteraction
} from '../../../interactions/services/interactionsService.ts'
import { updateProspectRecord } from '../../../../state_manager/state_update_actions/fundraising/index.ts'
import { fetchFundraisingById } from '../../services/fundraisingService.ts'
import { selectProspectById } from '../../../../state_manager/state_update_actions/fundraising/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  updateActiveRecord
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import {
  addInteraction,
  updateInteraction as updateInteractionAction,
  setInteractions,
  removeInteraction
} from '../../../../state_manager/state_update_actions/interactions/interaction_actions.ts'
import { ProspectFields } from '../../utils/fieldConfigs.ts'
import {
  getDefaultPipelineConfigForPipeline,
  isPipelineStageValidForPipeline
} from '../../utils/pipelineFundStageRules.ts'
import { PIPELINE_TYPE_FUNDRAISING } from '../../../../state_manager/globals.ts'
import { UserEntryFields } from '../../../../state_manager/data_models/user_entry.ts'
import { UNKNOWN_CONTACT } from '../../../../state_manager/constants/strings.ts'
import { TaskRecordFields, type TaskRecord } from '../../../../state_manager/data_models/task_record.ts'
import {
  InteractionRecordFields,
  type InteractionRecord
} from '../../../../state_manager/data_models/interaction_record.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store, type RootState } from '../../../../state_manager/store.ts'
import type { ProspectRecord } from '../../../../state_manager/interfaces/fundraising.ts'
import type { ProspectContactsByRole } from '../../utils/prospectContacts.ts'

const MemoizedTasksSection = React.memo(function MemoizedTasksSection(
  props: React.ComponentProps<typeof TasksSection>
) {
  return <TasksSection {...props} />
})

const MemoizedInteractionsSection = React.memo(function MemoizedInteractionsSection(
  props: React.ComponentProps<typeof InteractionsSection>
) {
  return <InteractionsSection {...props} />
})

const MemoizedProspectOverviewSection = React.memo(function MemoizedProspectOverviewSection(
  props: React.ComponentProps<typeof ProspectOverviewSection>
) {
  return <ProspectOverviewSection {...props} />
})

interface DrawerProps {
  prospect?: ProspectRecord | null
  // Passed by the drawer shell (MainTabContainer) to both drawers; the
  // existing-record drawer saves per field and does not read them.
  onSubmit?: (data: Record<string, unknown>) => Promise<void>
  loading?: boolean
  onRefreshInteractionsReady?: (refreshFn: () => Promise<void>) => void
  onRefreshTasksReady?: (refreshFn: () => Promise<void>) => void
}

export function Drawer({ prospect = null, onRefreshInteractionsReady, onRefreshTasksReady }: DrawerProps) {
  const location = useLocation()
  const dispatch = useAppDispatch()

  const { setRightMenuWidth, resetRightMenuWidth } = useRightMenu()

  const prospectId = prospect?.[ProspectFields.prospects__id] || prospect?.id
  const pipelineConfigs = useSelector((state: RootState) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: RootState) => state.globalConfigs?.pipeline_stages || [])
  const funds = useSelector((state: any) => state.globalConfigs?.funds || [])
  const pipelines = useSelector((state: any) => state.globalConfigs?.pipelines || [])

  const defaultFundraisingPipelineId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (p: { default?: boolean; type?: string; id?: string }) =>
        p.default === true && p.type === PIPELINE_TYPE_FUNDRAISING
    ) as { id?: string } | undefined
    return defaultPipeline?.id || null
  }, [pipelines])

  const searchParams = new URLSearchParams(location.search)
  const hideInteractions = searchParams.get('hideInteractions') === 'true'
  const initialTaskId = searchParams.get('taskId')

  const [selectedItem, setSelectedItem] = useState<TaskRecord | InteractionRecord | null>(null)
  const [selectedItemType, setSelectedItemType] = useState<DetailPanelItemType | null>(null)
  // The detail panel holds a task or an interaction; read the id the selected record carries.
  const selectedTaskId =
    selectedItem && TaskRecordFields.tasks__id in selectedItem ? selectedItem[TaskRecordFields.tasks__id] : undefined
  const selectedInteractionId =
    selectedItem && InteractionRecordFields.interactions__id in selectedItem
      ? selectedItem[InteractionRecordFields.interactions__id]
      : undefined
  const [detailMode, setDetailMode] = useState<DetailPanelMode>('view')
  const [isTaskInEditMode, setIsTaskInEditMode] = useState(false)
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)

  const [formData, setFormData] = useState<Record<string, unknown>>({})

  const [selectedContacts, setSelectedContacts] = useState<ProspectContactsByRole>({
    introduced_by: [],
    sourced_by: [],
    lead: []
  })

  useEffect(() => {
    const isDetailPanelOpen =
      (selectedItem || detailMode !== 'view') && selectedItemType !== 'task' && selectedItemType !== 'interaction'

    const isTaskOrInteractionEditing =
      (selectedItemType === 'task' || selectedItemType === 'interaction') &&
      (detailMode === 'add-task' ||
        detailMode === 'edit-task' ||
        detailMode === 'add-interaction' ||
        detailMode === 'edit-interaction' ||
        selectedItem)

    if (isDetailPanelOpen) {
      setRightMenuWidth('55%')
    } else if (isTaskOrInteractionEditing) {
      setRightMenuWidth('52.5%')
    } else {
      resetRightMenuWidth()
    }

    return () => {
      resetRightMenuWidth()
    }
  }, [selectedItem, detailMode, selectedItemType, setRightMenuWidth, resetRightMenuWidth])

  useEffect(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
    setIsTaskInEditMode(false)
  }, [prospectId])

  const prospectFromState = useSelector((state: any) => selectProspectById(state, prospectId))
  const prospectFromActiveRecords = useSelector((state: any) =>
    prospectId ? selectActiveRecordById(state, prospectId) : null
  )
  const currentProspect = prospectFromState || prospectFromActiveRecords || prospect

  const previousProspectIdRef = useRef<string | number | null>(null)

  useEffect(() => {
    const prospectIdString = prospectId?.toString()
    const previousProspectIdString = previousProspectIdRef.current?.toString()

    if (prospectIdString && prospectIdString !== previousProspectIdString && prospect) {
      dispatch(setActiveRecord({ uuid: prospectIdString, record: prospect }))
      dispatch(registerRecordUsage({ uuid: prospectIdString, context: 'drawer' }))
      previousProspectIdRef.current = prospectId
    }

    return () => {
      if (previousProspectIdString) {
        dispatch(unregisterRecordUsage({ uuid: previousProspectIdString, context: 'drawer' }))
      }
    }
  }, [prospectId, dispatch])

  const tasks = useMemo((): TaskRecord[] => {
    if (!currentProspect) return []
    return currentProspect[ProspectFields.prog__tasks] || []
  }, [currentProspect])

  useEffect(() => {
    if (initialTaskId && tasks && tasks.length > 0) {
      const targetTask = tasks.find((task) => task[TaskRecordFields.tasks__id]?.toString() === initialTaskId.toString())
      if (targetTask) {
        setSelectedItem(targetTask)
        setSelectedItemType('task')
        setDetailMode('view')
      }
    }
  }, [initialTaskId, tasks])

  useEffect(() => {
    if (currentProspect) {
      const initialData = {
        [ProspectFields.prospects__investor]: currentProspect[ProspectFields.prospects__investor],
        [ProspectFields.prospects__fund]: currentProspect[ProspectFields.prospects__fund],
        [ProspectFields.prospects__pipeline_origination]: currentProspect[ProspectFields.prospects__pipeline_origination],
        [ProspectFields.pipeline_prospects__stage]: currentProspect[ProspectFields.pipeline_prospects__stage],
        [ProspectFields.prospects__currency]: currentProspect[ProspectFields.prospects__currency],
        [ProspectFields.prospects__commitment]: currentProspect[ProspectFields.prospects__commitment],
        [ProspectFields.prospects__allocation_available]:
          currentProspect[ProspectFields.prospects__allocation_available]
      }
      setFormData(initialData)

      const contactsByRole: ProspectContactsByRole = {
        introduced_by: [],
        sourced_by: [],
        lead: []
      }

      const introducedByDetail = currentProspect[ProspectFields.contact_accounts__introduced_by]
      if (introducedByDetail && Array.isArray(introducedByDetail)) {
        contactsByRole.introduced_by = introducedByDetail.map((contact) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const sourcedByDetails = currentProspect[ProspectFields.contact_accounts__sourced_by]
      if (sourcedByDetails && Array.isArray(sourcedByDetails)) {
        contactsByRole.sourced_by = sourcedByDetails.map((contact) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const leadDetail = currentProspect[ProspectFields.contact_prospects__lead]
      if (leadDetail && Array.isArray(leadDetail)) {
        contactsByRole.lead = leadDetail.map((contact) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      setSelectedContacts(contactsByRole)
    }
  }, [currentProspect])

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => {
        const next: Record<string, any> = { ...prev, [field]: value }
        if (field === ProspectFields.prospects__fund) {
          const raw = value
          const fundId =
            raw != null && typeof raw === 'object' && 'value' in raw
              ? (raw as { value?: string }).value
              : typeof raw === 'string'
                ? raw
                : null
          const fundRow = fundId
            ? funds.find((f: { id?: string; value?: string }) => (f.id ?? f.value) === fundId)
            : undefined
          const po = fundRow?.pipeline_origination
          next[ProspectFields.prospects__pipeline_origination] =
            typeof po === 'string' && po.length > 0 ? po : null
          const effectivePipeline =
            (typeof po === 'string' && po.length > 0 ? po : null) || defaultFundraisingPipelineId
          const currentStageId = prev[ProspectFields.pipeline_prospects__stage] as string | undefined

          if (
            effectivePipeline &&
            isPipelineStageValidForPipeline(currentStageId, effectivePipeline, pipelineConfigs)
          ) {
            // keep stage; pipeline origination already updated above
          } else {
            let resolved =
              effectivePipeline &&
              getDefaultPipelineConfigForPipeline(effectivePipeline, pipelineConfigs, pipelineStages)
            if (!resolved && defaultFundraisingPipelineId) {
              resolved = getDefaultPipelineConfigForPipeline(
                defaultFundraisingPipelineId,
                pipelineConfigs,
                pipelineStages
              )
            }
            if (resolved) {
              next[ProspectFields.pipeline_prospects__stage] = resolved.configId
              next[ProspectFields.pipeline_prospects__stage__label] = resolved.label
            } else {
              next[ProspectFields.pipeline_prospects__stage] = ''
              next[ProspectFields.pipeline_prospects__stage__label] = ''
            }
          }
        }
        return next
      })
    },
    [funds, defaultFundraisingPipelineId, pipelineConfigs, pipelineStages]
  )

  const handlePatchSuccess = useCallback(
    (patchRecordId: string, updates: Record<string, any>) => {
      if (updates[ProspectFields.pipeline_prospects__stage]) {
        const pipelineConfigId = updates[ProspectFields.pipeline_prospects__stage]
        const config = pipelineConfigs.find((c: any) => c.id === pipelineConfigId)
        if (config) {
          const stage = pipelineStages.find((s: any) => s.id === config.stage)
          if (stage && stage.name) {
            updates['pipeline_prospects__stage__label'] = stage.name
          }
        }
      }

      setFormData((prev) => ({ ...prev, ...updates }))

      if (prospectId) {
        dispatch(
          updateProspectRecord({
            prospectId: prospectId.toString(),
            updates
          })
        )
      }

    },
    [prospectId, dispatch, pipelineConfigs, pipelineStages]
  )

  const handleTaskSelect = useCallback((task: any) => {
    if (task === null) {
      setSelectedItem(null)
      setSelectedItemType(null)
      setDetailMode('view')
    } else {
      setSelectedItem(task)
      setSelectedItemType('task')
      setDetailMode('view')
    }
  }, [])

  const handleAddTask = useCallback(() => {
    setDetailMode('add-task')
    setSelectedItem(null)
    setSelectedItemType('task')
  }, [])

  const handleTaskEditModeChange = useCallback((isInEditMode: boolean) => {
    setIsTaskInEditMode(isInEditMode)
  }, [])

  const handleInteractionSelect = useCallback((interaction: any) => {
    setSelectedItem(interaction)
    setSelectedItemType('interaction')
    setDetailMode('view')
  }, [])

  const handleAddInteraction = useCallback(() => {
    setDetailMode('add-interaction')
    setSelectedItem(null)
    setSelectedItemType('interaction')
  }, [])

  const handleDetailClose = useCallback(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
  }, [])

  const handleDetailEdit = useCallback((item: any, itemType: string) => {
    if (itemType === 'task') {
      setDetailMode('edit-task')
    } else if (itemType === 'interaction') {
      setDetailMode('edit-interaction')
    }
  }, [])

  const handleDetailDelete = useCallback(
    async (id: string) => {
      if (!prospectId || !id) {
        throw new Error('Prospect ID is missing')
      }

      if (selectedItemType === 'task') {
        try {
          await dispatch(deleteTaskThunk({ dealId: prospectId, taskId: id, recordType: 'prospect' })).unwrap()
          setTimeout(() => {
            handleDetailClose()
          }, 100)
        } catch (error) {
          console.error('Error in handleDetailDelete:', error)
          setTimeout(() => {
            handleDetailClose()
          }, 100)
          throw error
        }
        return
      }

      const interactionId = id
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteInteraction(prospectId, interactionId, currentToken)
        dispatch(removeInteraction(prospectId, interactionId))
        const refreshedInteractions = await fetchInteractionsForProspect(prospectId, {}, currentToken)
        dispatch(setInteractions(prospectId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelete:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [prospectId, dispatch, handleDetailClose, selectedItemType]
  )

  const handleDetailDelink = useCallback(
    async (interactionId: string) => {
      if (!prospectId || !interactionId) {
        throw new Error('Prospect ID or Interaction ID is missing')
      }

      try {
        const currentToken = getAccessToken(store.getState().auth)
        await delinkInteraction(prospectId, interactionId, 'prospect', currentToken)
        dispatch(removeInteraction(prospectId, interactionId))
        const refreshedInteractions = await fetchInteractionsForProspect(prospectId, {}, currentToken)
        dispatch(setInteractions(prospectId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelink:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [prospectId, dispatch, handleDetailClose]
  )

  const handleRefreshTasks = useCallback(async () => {
    if (refreshTasksRef.current) {
      await refreshTasksRef.current()
    }
  }, [])

  const handleDetailSubmit = async (data: any, mode: string) => {
    if (!prospectId || prospectId === 'new') {
      console.warn('Cannot save task/interaction: Prospect ID is not available')
      return
    }

    try {
      if (mode === 'add-task') {
        const taskData = {
          ...data,
          [TaskRecordFields.task_prospects__prospect_id]: prospectId
        }
        const investorValue = prospect?.[ProspectFields.prospects__investor]
        const accountId =
          typeof investorValue === 'object'
            ? investorValue?.value ?? investorValue?.id ?? investorValue?.accounts__id ?? null
            : investorValue ?? null
        const result = await dispatch(
          createTask({
            dealId: prospectId,
            taskData,
            recordType: 'prospect',
            accountId
          }) as any
        )

        if (result.payload?.task && currentProspect) {
          const existingTasks = currentProspect[ProspectFields.prog__tasks] || []
          const updatedTasks = [result.payload.task, ...existingTasks]

          dispatch(
            updateActiveRecord({
              uuid: prospectId.toString(),
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )

          dispatch(
            updateProspectRecord({
              prospectId: prospectId.toString(),
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'edit-task') {
        const taskData = {
          ...data
        }
        const taskId = selectedTaskId
        // Edit mode is only reachable from an open task panel; a missing id means the
        // panel state is inconsistent, and a PUT /tasks/undefined would 4xx anyway.
        if (!taskId) {
          throw new Error('Task ID is missing')
        }

        const result = await dispatch(
          updateTask({
            dealId: prospectId,
            taskId,
            taskData,
            recordType: 'prospect'
          }) as any
        )

        if (result.payload?.task && currentProspect) {
          const existingTasks: TaskRecord[] = currentProspect[ProspectFields.prog__tasks] || []
          const taskIdFromResult = result.payload.task[TaskRecordFields.tasks__id] || taskId
          const updatedTasks = existingTasks.map((t) => {
            const existingTaskId = t?.[TaskRecordFields.tasks__id]
            if (existingTaskId?.toString() === taskIdFromResult?.toString()) {
              return result.payload.task
            }
            return t
          })

          dispatch(
            updateActiveRecord({
              uuid: prospectId.toString(),
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )

          dispatch(
            updateProspectRecord({
              prospectId: prospectId.toString(),
              updates: {
                [ProspectFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'add-interaction') {
        const interactionData = {
          ...data
        }
        const investorValue = prospect?.[ProspectFields.prospects__investor]
        const accountId =
          typeof investorValue === 'object'
            ? investorValue?.value ?? investorValue?.id ?? investorValue?.accounts__id ?? null
            : investorValue ?? null
        const currentToken = getAccessToken(store.getState().auth)
        const createdInteraction = await createInteraction(
          prospectId,
          interactionData,
          currentToken,
          'prospect',
          accountId
        )

        dispatch(addInteraction(prospectId, createdInteraction))
      } else if (mode === 'edit-interaction') {
        const interactionData = {
          ...data
        }
        const interactionId = selectedInteractionId
        if (!interactionId) {
          throw new Error('Interaction ID is missing')
        }
        const currentToken = getAccessToken(store.getState().auth)
        const updatedInteraction = await updateInteraction(
          prospectId,
          interactionId,
          interactionData,
          currentToken,
          'prospect'
        )

        dispatch(updateInteractionAction(prospectId, interactionId, updatedInteraction))
      }

      setDetailMode('view')
    } catch (error) {
      console.error('Failed to save:', error)
      throw error
    }
  }

  const handleContactsChange = (updatedContacts: ProspectContactsByRole) => {
    setSelectedContacts(updatedContacts)
  }

  const isFullWidthMode = useMemo(() => {
    return (
      (selectedItemType === 'task' || selectedItemType === 'interaction') &&
      (selectedItem ||
        detailMode === 'add-task' ||
        detailMode === 'add-interaction' ||
        detailMode === 'edit-task' ||
        detailMode === 'edit-interaction')
    )
  }, [selectedItemType, selectedItem, detailMode])

  const isDetailPanelOpen = useMemo(
    () => (selectedItem || detailMode !== 'view') && selectedItemType !== 'task' && selectedItemType !== 'interaction',
    [selectedItem, detailMode, selectedItemType]
  )

  const tasksSectionProps = useMemo(
    () => ({
      recordId: prospectId,
      recordType: 'prospect' as const,
      onTaskSelect: handleTaskSelect,
      selectedTaskId: selectedItemType === 'task' ? selectedTaskId : null,
      onAddTask: handleAddTask,
      onEditModeChange: handleTaskEditModeChange,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshTasksRef.current = refreshFn
        if (onRefreshTasksReady) {
          onRefreshTasksReady(refreshFn)
        }
      }
    }),
    // eslint-disable-next-line
    [
      prospectId,
      selectedItemType,
      selectedTaskId,
      handleTaskSelect,
      handleAddTask,
      handleTaskEditModeChange,
      onRefreshTasksReady
    ]
  )

  const interactionsSectionProps = useMemo(
    () => ({
      recordId: prospectId,
      recordType: 'prospect' as const,
      onInteractionSelect: handleInteractionSelect,
      selectedInteractionId: selectedItemType === 'interaction' ? selectedInteractionId : null,
      onAddInteraction: handleAddInteraction,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshInteractionsRef.current = refreshFn
        if (onRefreshInteractionsReady) {
          onRefreshInteractionsReady(refreshFn)
        }
      }
    }),
    // eslint-disable-next-line
    [
      prospectId,
      selectedItemType,
      selectedInteractionId,
      handleInteractionSelect,
      handleAddInteraction,
      onRefreshInteractionsReady
    ]
  )

  const prospectOverviewSectionProps = useMemo(
    () => ({
      prospect: currentProspect,
      formData,
      handleFieldChange,
      onPatchSuccess: handlePatchSuccess,
      selectedContacts,
      onContactsChange: handleContactsChange,
      isDrawer: true,
      cardProps: { className: 'mb-4' },
      recordId: prospectId
    }),
    [currentProspect, formData, handleFieldChange, handlePatchSuccess, selectedContacts, prospectId]
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        data-debug='drawer-existing-container'
        data-component='DrawerExisting'
        data-prospect-id={prospectId}
        data-selected-item-type={selectedItemType}
        data-detail-mode={detailMode}
        data-hide-interactions={hideInteractions}
        className='h-full'
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <div data-debug='drawer-unified-layout' className='flex flex-col h-full'>
          <div data-debug='drawer-overview-compact' className='flex-shrink-0'>
            <MemoizedProspectOverviewSection {...prospectOverviewSectionProps} />
          </div>

          <Row data-debug='drawer-content-row' gutter={[16, 16]} className='h-full flex-1 min-h-0'>
            <Col span={isFullWidthMode ? 10 : isDetailPanelOpen ? 10 : 24} className='h-full flex flex-col min-h-0'>
              <div data-debug='drawer-tasks-section' className='mb-1 flex-shrink-0'>
                <MemoizedTasksSection {...tasksSectionProps} />
              </div>

              {!hideInteractions && (
                <div data-debug='drawer-interactions-section' className='flex flex-col flex-1 min-h-0'>
                  <div className='h-full min-h-[280px]'>
                    <MemoizedInteractionsSection {...interactionsSectionProps} />
                  </div>
                </div>
              )}
            </Col>

            {(isFullWidthMode || isDetailPanelOpen) && (
              <Col span={14} className='h-full flex flex-col min-h-0'>
                <DetailPanel
                  selectedItem={selectedItem}
                  itemType={selectedItemType}
                  mode={detailMode}
                  onClose={handleDetailClose}
                  onEdit={handleDetailEdit}
                  onSubmit={handleDetailSubmit}
                  onDelete={handleDetailDelete}
                  onDelink={handleDetailDelink}
                  onRefreshInteractions={async () => {
                    if (refreshInteractionsRef.current) {
                      await refreshInteractionsRef.current()
                    }
                  }}
                  onRefreshTasks={handleRefreshTasks}
                />
              </Col>
            )}
          </Row>
        </div>
      </div>
    </DndProvider>
  )
}
